import { forwardRef, RefObject } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Portal,
  Text,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  ArrowRightOutline,
  DropdownInput,
  DropdownOption,
  EmptyState,
  shadowStyles,
  textStyles,
} from '@aslanpay/portal-common-lib';

import { useImportEmployeesStore } from 'app/store';

import { importEmployeesPayCalendarFormSchema } from 'app/utils/schemas';

import { getPayCalendarsPath } from 'app/utils/routes';

import { SelectPayCalendarFormFields, SelectPayCalendarStepProps } from '../types';

const SelectPayCalendar = forwardRef<HTMLElement, SelectPayCalendarStepProps>(
  ({ id, onStepAction, payCalendars }, ref) => {
    const { importEmployeesData, setImportEmployeesData } = useImportEmployeesStore.getState();

    const {
      control,
      handleSubmit,
      formState: { errors, isValid },
    } = useForm<SelectPayCalendarFormFields>({
      mode: 'onBlur',
      defaultValues: { payCalendarId: importEmployeesData.payCalendarId },
      resolver: yupResolver(importEmployeesPayCalendarFormSchema),
    });

    const handleNextStep = ({ payCalendarId }: SelectPayCalendarFormFields) => {
      const payCalendarName =
        payCalendars.find((payCalendar) => payCalendar.id === payCalendarId)?.name || '';

      setImportEmployeesData({
        ...importEmployeesData,
        payCalendarId,
        payCalendarName,
      });

      onStepAction();
    };

    const payCalendarOptions: DropdownOption[] =
      payCalendars.map((payCalendar) => ({
        value: payCalendar.id,
        text: payCalendar.name,
      })) || [];

    return payCalendars.length === 0 ? (
      <EmptyState
        full={false}
        text="You need to have at least one pay calendar in order to proceed."
        link={getPayCalendarsPath(importEmployeesData.companyId)}
        linkText="Go to pay calendars"
      />
    ) : (
      <Flex
        flexDir="column"
        maxW="572px"
        alignSelf="center"
        gap="30px"
        borderRadius="10px"
        boxShadow={shadowStyles.dropdownMenu}
      >
        <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
          <FormControl isInvalid={!!errors?.payCalendarId?.message} isRequired>
            <HStack
              minH="65px"
              px="40px"
              py="20px"
              borderBottom="1px solid"
              borderColor="brand.lightGray1"
              alignItems="center"
              justify="space-between"
            >
              <FormLabel as="h5" sx={{ ...textStyles.h5Bold }} margin="0">
                Select the pay calendar
              </FormLabel>
            </HStack>
            <Flex flexDir="column" px="40px" py="20px" gap="20px">
              <Text color="brand.mutedPurple" textStyle="smallMedium">
                Please select the pay calendar you want to upload your employees&apos; data to.
              </Text>

              <Flex flexDir="column">
                <Controller
                  control={control}
                  name="payCalendarId"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DropdownInput
                      placeholder="Select the pay calendar"
                      defaultValue={payCalendarOptions.find(
                        (payCalendar) => payCalendar.value === value
                      )}
                      options={payCalendarOptions}
                      onChange={onChange}
                      onBlur={onBlur}
                      isSearchable
                      searchPlaceholder="Search pay calendars..."
                    />
                  )}
                />
                <FormErrorMessage>{errors?.payCalendarId?.message}</FormErrorMessage>
              </Flex>
              <Text alignSelf="flex-start" color="system.error" textStyle="extraSmallMedium">
                * mandatory
              </Text>
            </Flex>
          </FormControl>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </form>
      </Flex>
    );
  }
);

SelectPayCalendar.displayName = 'SelectPayCalendar';

export default SelectPayCalendar;
